import { createSelector } from 'reselect'
import {
    getRotation,
} from 'redux/appliedFilters/selectors'

import {
    Position,
    Rectangle,
} from 'helpers/math'
import { getUserScale } from 'redux/appliedFilters/selectors'
import { Vector } from '../../helpers/math.js'
import { isCurrentMediaSourceLoaded } from '../currentMedia/selectors'

export const getCanvas = state => state.canvas

const scaleSelector = (type) => createSelector(
    getCanvas,
    (state, viewId) => viewId,
    (canvas, viewId) => {
        if(! canvas.projections || ! canvas.projections[viewId])
        {
            return 1
        }

        return canvas.projections[viewId][type]
    }
)

export const getFrameScale = scaleSelector('frameScale')

export const getViewScale = scaleSelector('viewScale')

export const getScale = createSelector(
    getFrameScale,
    getViewScale,
    getUserScale,
    (frameScale, viewScale, userScale) => frameScale * viewScale * userScale,
)

export const getCurrentImageRect = createSelector(
    getCanvas,
    canvas => canvas.imageRect
)

export const getProjection = createSelector(
    getCanvas,
    (state, viewId) => viewId,
    (canvas, viewId) => canvas.projections && canvas.projections[viewId]
)

export const getScaledCurrentImageRect = createSelector(
    getCurrentImageRect,
    getScale,
    (imageRect, scale) => imageRect.scale(scale)
)

export const computeViewData = createSelector(
    getScaledCurrentImageRect,
    getRotation,
    getProjection,
    (imageRect, rotation, { frame, viewScale }) => {
        const offset = new Position(
            (imageRect.width / 2 - imageRect.position.x),
            (imageRect.height / 2 - imageRect.position.y),
        )

        const frameWithoutRotationApplied = frame.scale(viewScale)
        frame = frameWithoutRotationApplied.rotate(rotation)

        const frameCenter = new Position(
            Math.round(frame.width / 2),
            Math.round(frame.height / 2)
        )

        return {
            frameWithoutRotationApplied,
            frame,
            offset,
            width: imageRect.width,
            height: imageRect.height,
            viewRect: new Rectangle(
                frame.width,
                frame.height,
                offset.move(frameCenter.convertToVector().invert()),
            ),
            rotation,
        }
    }
)

export const computeMinimapViewData = createSelector(
    getCurrentImageRect,
    getUserScale,
    getRotation,
    (state, viewId, parentViewId) => getProjection(state, parentViewId),
    (state, viewId, parentViewId) => getProjection(state, viewId),
    (imageRect, userScale, rotation, parentProjection, minimapProjection) => {

        const { frame: parentFrame, frameScale: parentFrameScale } = parentProjection
        const { frame: minimapFrame, frameScale: minimapFrameScale } = minimapProjection

        imageRect = imageRect.scale(minimapFrameScale)

        const imageRectCenter = new Position(imageRect.width / 2, imageRect.height / 2)
        const offset = imageRectCenter.move(imageRect.position.convertToVector().invert().rotate(rotation))
        const adaptedParentToMinimapFrame = parentFrame.unscale(parentFrameScale / minimapFrameScale * userScale)

        const viewZone = new Rectangle(
            adaptedParentToMinimapFrame.width,
            adaptedParentToMinimapFrame.height,
            offset.move(new Vector(
                (minimapFrame.width - imageRect.width) / 2,
                (minimapFrame.height - imageRect.height) / 2,
            )),
        )

        return {
            image: imageRect,
            viewingWindow: {
                width: viewZone.width,
                height: viewZone.height,
                position: viewZone.position,
                offset: {
                    x: viewZone.width / 2,
                    y: viewZone.height / 2,
                },
            },
        }
    }
)

export const isProjectionInitialized = createSelector(
    getProjection,
    projection => projection !== null && projection !== undefined,
)

export const isCurrentImageRectInitialized = createSelector(
    getCurrentImageRect,
    imageRect => imageRect !== null && imageRect !== undefined,
)

export const isImageReadyToBeDisplayed = createSelector(
    isCurrentMediaSourceLoaded,
    isProjectionInitialized,
    isCurrentImageRectInitialized,
    (isSourceLoaded, isProjectionInitialized, isCurrentImageRectInitialized) => isSourceLoaded && isProjectionInitialized && isCurrentImageRectInitialized
)

export const mainViewId = "default"
